import { resolveParameterDefault } from './parameterDefaults';
import { omitDisallowedParameterValues } from './parameterOptions';
import { isReservedParameterName } from './reservedParameters';
const resolveDefinitionDefaults = (definitions, now, timezone) => Object.entries(definitions).reduce((acc, [key, definition]) => {
    const value = resolveParameterDefault(definition, now, timezone ?? undefined);
    return value === undefined ? acc : { ...acc, [key]: value };
}, {});
export const getEffectiveParameterDefinitions = ({ projectDefinitions, exploreDefinitions, }) => ({
    ...projectDefinitions,
    ...exploreDefinitions,
});
export const resolveFallbackParameterValues = ({ projectDefinitions, exploreDefinitions, virtualViewSavedValues, now, timezone, }) => ({
    ...resolveDefinitionDefaults(projectDefinitions, now, timezone),
    ...resolveDefinitionDefaults(exploreDefinitions, now, timezone),
    ...omitDisallowedParameterValues(virtualViewSavedValues, getEffectiveParameterDefinitions({
        projectDefinitions,
        exploreDefinitions,
    })),
});
// A chart's saved value applies only when the dashboard has no value and the definition no default
export const canUseChartSavedParameterValue = ({ key, dashboardValues, definitions, }) => dashboardValues[key] === undefined &&
    definitions[key]?.default === undefined;
// Targeted key: dashboard value, else chart-saved only when the definition has no default.
// Excluded key: chart-saved value only.
export const getDashboardTileParameterOverrides = ({ definitions, dashboardValues: rawDashboardValues, chartSavedValues: rawChartSavedValues, excludedKeys, }) => {
    // Values outside a parameter's fixed options count as unset
    const dashboardValues = omitDisallowedParameterValues(rawDashboardValues, definitions);
    const chartSavedValues = omitDisallowedParameterValues(rawChartSavedValues, definitions);
    const excluded = new Set(excludedKeys);
    const keys = new Set([
        ...Object.keys(chartSavedValues),
        ...Object.keys(dashboardValues),
    ]);
    return [...keys].reduce((acc, key) => {
        const chartSavedValue = chartSavedValues[key];
        if (excluded.has(key)) {
            return chartSavedValue === undefined
                ? acc
                : { ...acc, [key]: chartSavedValue };
        }
        const dashboardValue = dashboardValues[key];
        if (dashboardValue !== undefined) {
            return { ...acc, [key]: dashboardValue };
        }
        if (chartSavedValue === undefined ||
            !canUseChartSavedParameterValue({
                key,
                dashboardValues,
                definitions,
            })) {
            return acc;
        }
        return { ...acc, [key]: chartSavedValue };
    }, {});
};
export const resolveDashboardTileParameters = ({ fallbackSources, ...inputs }) => ({
    ...resolveFallbackParameterValues(fallbackSources),
    ...getDashboardTileParameterOverrides({
        ...inputs,
        definitions: getEffectiveParameterDefinitions(fallbackSources),
    }),
});
export var DashboardTileParameterSource;
(function (DashboardTileParameterSource) {
    DashboardTileParameterSource["DASHBOARD"] = "dashboard";
    DashboardTileParameterSource["CHART"] = "chart";
    DashboardTileParameterSource["DEFAULT"] = "default";
})(DashboardTileParameterSource || (DashboardTileParameterSource = {}));
// Where a tile's value for one parameter comes from; default covers the whole fallback chain.
export const getDashboardTileParameterSource = ({ key, ...inputs }) => {
    if (getDashboardTileParameterOverrides(inputs)[key] === undefined) {
        return DashboardTileParameterSource.DEFAULT;
    }
    const dashboardValues = omitDisallowedParameterValues(inputs.dashboardValues, inputs.definitions);
    return !inputs.excludedKeys.includes(key) &&
        dashboardValues[key] !== undefined
        ? DashboardTileParameterSource.DASHBOARD
        : DashboardTileParameterSource.CHART;
};
// Referenced keys, paired with whether each referencing tile resolves a value with its own inputs
const getKeysResolvedPerTile = ({ tiles, definitions, }) => tiles.reduce((acc, { parameterReferences, virtualViewSavedValues, ...inputs }) => {
    const overrides = getDashboardTileParameterOverrides({
        ...inputs,
        definitions,
    });
    const virtualViewValues = omitDisallowedParameterValues(virtualViewSavedValues, definitions);
    new Set(parameterReferences).forEach((key) => {
        if (isReservedParameterName(key))
            return;
        acc.set(key, [
            ...(acc.get(key) ?? []),
            overrides[key] !== undefined ||
                definitions[key]?.default !== undefined ||
                virtualViewValues[key] !== undefined,
        ]);
    });
    return acc;
}, new Map());
// A key is missing when any referencing tile has no dashboard, default, chart-saved or virtual-view value.
export const getMissingRequiredDashboardParameters = (inputs) => [...getKeysResolvedPerTile(inputs)]
    .filter(([, resolved]) => resolved.some((has) => !has))
    .map(([key]) => key);
//# sourceMappingURL=dashboardTileParameters.js.map